import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuth } from "@/stores/auth";


const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/LoginView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/invite",
    name: "Invite",
    component: () => import("@/views/InviteView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/",
    redirect: "/sessions",
  },
  {
    path: "/sessions",
    name: "SessionList",
    component: () => import("@/views/SessionListView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/sessions/:id",
    name: "ChatDetail",
    component: () => import("@/views/ChatDetailView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/messages",
    name: "Messages",
    component: () => import("@/views/MessagesView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/views/SettingsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/tenants",
    name: "Tenants",
    component: () => import("@/views/TenantsView.vue"),
    meta: { requiresAuth: true, superAdminOnly: true },
  },
  {
    path: "/accounts",
    name: "Accounts",
    component: () => import("@/views/AccountsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/announcements",
    name: "Announcements",
    component: () => import("@/views/AnnouncementsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Org-specific session management page (accessible via chatbot.congkong.net/:siteId redirect)
    path: "/:siteId([a-z0-9-]+)",
    name: "OrgSessions",
    component: () => import("@/views/OrgSessionsView.vue"),
    meta: { requiresAuth: true, superAdminOnly: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const { isAuthenticated, authLoading } = useAuth();

  // Wait for auth to initialize
  if (authLoading.value) {
    await new Promise<void>((resolve) => {
      const stop = setInterval(() => {
        if (!authLoading.value) {
          clearInterval(stop);
          resolve();
        }
      }, 50);
    });
  }

  if (to.meta.requiresAuth && !isAuthenticated.value) {
    next({ name: "Login", query: { redirect: to.fullPath } });
  } else if (to.name === "Login" && isAuthenticated.value) {
    next({ name: "SessionList" });
  } else if (to.meta.superAdminOnly && !useAuth().isSuperAdmin.value) {
    next({ name: "SessionList" });
  } else {
    next();
  }
});

router.onError((err, to) => {
  if (err.message.includes("Failed to fetch dynamically imported module")) {
    window.location.href = to.fullPath;
  }
});

export default router;
